"use client";

import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { usePrivy } from "@privy-io/react-auth";
import { Wallet, LogIn, Shield, ArrowLeftFromLine } from "lucide-react";
import {
  doc,
  setDoc,
  collection,
  query,
  where,
  getDocs,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../../firebase";
import { useDispatch } from "react-redux";
import { setUser, clearUser } from "../slices/userSlices";

const Login: React.FC = () => {
  const { ready, authenticated, user, login, logout } = usePrivy();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Onboarding state
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [username, setUsername] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Helper: Get EOA wallet address (embedded wallet)
  const getEOAWalletAddress = (): string | null => {
    if (!user?.linkedAccounts) return null;

    const eoa = user.linkedAccounts.find(
      (acc) => acc.type === "wallet" && acc.chainType === "ethereum"
    ) as any;
    return eoa?.address || null;
  };

  // Helper: Get Smart Wallet address (optional)
  const getSmartWalletAddress = (): string | null => {
    return (
      user?.linkedAccounts?.find((acc) => acc.type === "smart_wallet")
        ?.address || null
    );
  };

  // Use EOA as primary user ID (recommended)
  const userWalletAddress = getEOAWalletAddress();

  // Debug
  console.log("User Object:", user);
  console.log("EOA Wallet:", userWalletAddress);
  console.log("Smart Wallet:", getSmartWalletAddress());

  // Send email via your backend
  const sendEmail = async (
    email: string,
    name: string,
    templateId: string,
    endpoint: string
  ) => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_SERVERURL}/${endpoint}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, name }),
          credentials: "include",
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.details || `API error: ${response.statusText}`
        );
      }

      const data = await response.json();
      console.log(`${endpoint} email sent:`, data);
    } catch (err) {
      console.error(`Failed to send ${endpoint} email:`, err);
      setError("Failed to send email. Please try again.");
    }
  };

  // ──────────────────────────────────────────────────────────────────────
  // 1. Add a new ref to know when we should stop polling
  // ──────────────────────────────────────────────────────────────────────
  const hasCheckedUser = useRef(false);
  const pollInterval = useRef<NodeJS.Timeout | null>(null);

  // ──────────────────────────────────────────────────────────────────────
  // 2. Replace the old useEffect with this one
  // ──────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!ready || !authenticated) {
      // Reset when not ready / not logged in
      hasCheckedUser.current = false;
      if (pollInterval.current) clearInterval(pollInterval.current);
      return;
    }

    const startPolling = () => {
      // Run immediately
      checkUser();

      // Then every 5 seconds
      pollInterval.current = setInterval(() => {
        checkUser();
      }, 5_000);
    };

    const checkUser = async () => {
      // If we already succeeded → stop polling
      if (hasCheckedUser.current) {
        if (pollInterval.current) clearInterval(pollInterval.current);
        return;
      }

      const walletAddress = getEOAWalletAddress();
      if (!walletAddress) {
        // Wallet not ready yet → keep polling
        return;
      }

      // Wallet is finally there → stop polling & continue
      hasCheckedUser.current = true;
      if (pollInterval.current) clearInterval(pollInterval.current);

      try {
        // ── SAME LOGIC YOU ALREADY HAD (email lookup, Firestore, etc.) ──
        let email: string | null = null;
        for (const acc of user!.linkedAccounts) {
          if (acc.type === "email") email = acc.address;
          else if (acc.type === "google_oauth") email = acc.email;
        }

        if (!email) {
          setError("No email found. Please link an email.");
          setShowOnboarding(true);
          return;
        }

        const q = query(collection(db, "users"), where("email", "==", email));
        const snapshot = await getDocs(q);

        if (!snapshot.empty) {
          const userDoc = snapshot.docs[0];
          const userData = userDoc.data();

          dispatch(
            setUser({
              uid: walletAddress,
              email: userData.email || null,
              phone: userData.phone || null,
              wallet: walletAddress,
              smartWallet: getSmartWalletAddress(),
              username: userData.username,
              joinedAt: userData.joinDate?.toDate?.().toISOString() || null,
            })
          );

          await sendEmail(email, userData.username, "1", "authenticate-user");
          navigate("/dashboard", { replace: true });
        } else {
          setShowOnboarding(true);
        }
      } catch (err) {
        console.error("Error checking user:", err);
        setError("Error checking user. Please try again.");
      }
    };

    startPolling();

    // ── Cleanup on unmount / auth change
    return () => {
      if (pollInterval.current) clearInterval(pollInterval.current);
    };
  }, [ready, authenticated, user, navigate, dispatch]);

  // Handle onboarding
  const handleOnboarding = async () => {
    const cleanUsername = username.trim().toLowerCase();
    if (
      !cleanUsername ||
      cleanUsername.length > 8 ||
      /\s/.test(cleanUsername)
    ) {
      setError("Invalid username. Max 8 chars, no spaces.");
      return;
    }

    const walletAddress = getEOAWalletAddress();
    if (!walletAddress) {
      setError("No wallet found. Please reconnect.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      // Check username availability
      const q = query(
        collection(db, "users"),
        where("username", "==", cleanUsername)
      );
      const snapshot = await getDocs(q);
      if (!snapshot.empty) {
        setError("Username taken. Try another.");
        setLoading(false);
        return;
      }

      // Get email
      let email: string | null = null;
      for (const acc of user!.linkedAccounts) {
        if (acc.type === "email") email = acc.address;
        else if (acc.type === "google_oauth") email = acc.email;
      }

      if (!email) {
        setError("No email linked. Please add one.");
        setLoading(false);
        return;
      }

      // Save user with EOA address as doc ID
      const userRef = doc(db, "users", walletAddress);
      await setDoc(userRef, {
        username: cleanUsername,
        email,
        phone: null,
        wallet: walletAddress,
        smartWallet: getSmartWalletAddress(),
        joinDate: serverTimestamp(),
      });

      dispatch(
        setUser({
          uid: walletAddress,
          email,
          phone: null,
          wallet: walletAddress,
          smartWallet: getSmartWalletAddress(),
          username: cleanUsername,
          joinedAt: new Date().toISOString(),
        })
      );

      await sendEmail(email, cleanUsername, "2", "onboard-user");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      console.error("Onboarding error:", err);
      setError("Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    dispatch(clearUser());
    await logout();
  };

  const paymentLogos = [
    { src: "/applepay-logo.png", alt: "Apple Pay", scale: "scale-150 invert" },
    { src: "/googlepay-logo.png", alt: "Google Pay", scale: "scale-100" },
    { src: "/cashapp-logo.png", alt: "Cash App", scale: "scale-100" },
    { src: "/Stripe_Logo.png", alt: "Stripe", scale: "scale-100" },
    { src: "/mastercard-logo.png", alt: "Mastercard", scale: "scale-120" },
    { src: "/coinbase.svg", alt: "Coinbase", scale: "scale-120" },
    { src: "/crossmint-light.png", alt: "Crossmint", scale: "scale-120" },
  ];

  if (!ready) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] text-white">
      <div className="flex flex-col lg:flex-row justify-center items-center  ">
        {/* Left Side */}
        <div className="lg:w-1/2 flex flex-col justify-center items-center p-4 sm:p-8 lg:p-16 space-y-6 sm:space-y-8">
          <div className="flex items-center space-x-3 sm:space-x-4">
            <div className="p-3 sm:p-4 bg-[#61CE70]/20 rounded-2xl">
              <Wallet className="w-8 h-8 sm:w-12 sm:h-16 text-[#61CE70]" />
            </div>
            <img
              src="/logo.png"
              alt="logo"
              className="w-full h-20 md:h-32 object-contain"
            />
          </div>
          <div className="text-center lg:text-left space-y-4 max-w-full sm:max-w-md">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-semibold">
              Securely Manage Your Finances
            </h2>
            <p className="text-gray-400 text-base sm:text-lg">
              Connect your wallet, add funds, and transfer assets with ease. Our
              platform ensures top-notch security and seamless transactions.
            </p>
            <div className="flex items-center justify-center lg:justify-start space-x-2 text-xs sm:text-sm">
              <span className="text-[#61CE70]">Trusted by thousands</span>
              <span className="text-gray-400">| Secure & Fast</span>
            </div>
          </div>
        </div>

        {/* Right Side */}
        <div className="lg:w-1/2 flex justify-center items-center p-4 sm:p-8 lg:p-16">
          <div className="w-full max-w-full sm:max-w-md bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-gray-700/50 rounded-3xl p-6 sm:p-8 backdrop-blur-sm shadow-2xl shadow-[#61CE70]/20">
            {ready && authenticated ? (
              <>
                {showOnboarding ? (
                  <div className="space-y-4 sm:space-y-6">
                    <h3 className="textLg sm:text-xl font-bold text-white text-center">
                      Complete Your Onboarding
                    </h3>
                    <p className="text-gray-400 text-sm sm:text-base text-center">
                      Choose a unique username (max 8 chars, no spaces).
                    </p>
                    <input
                      type="text"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      placeholder="Enter username"
                      maxLength={8}
                      className="w-full px-4 py-2 rounded-xl bg-gray-800 text-white focus:outline-none focus:ring-2 focus:ring-[#61CE70]"
                    />
                    {error && (
                      <p className="text-red-500 text-sm text-center">
                        {error}
                      </p>
                    )}
                    <button
                      onClick={handleOnboarding}
                      disabled={loading}
                      className="w-full bg-gradient-to-r from-[#61CE70] to-[#50B060] text-white font-medium py-2 sm:py-3 px-4 sm:px-6 rounded-xl transition-all duration-300 hover:shadow-lg hover:shadow-[#61CE70]/30 hover:scale-105 disabled:opacity-50"
                    >
                      {loading ? "Saving..." : "Submit & Continue"}
                    </button>
                  </div>
                ) : (
                  <div className="text-center space-y-4 sm:space-y-6">
                    <h3 className="textLg sm:text-xl font-bold text-white">
                      Welcome,{" "}
                      {user?.email?.address || userWalletAddress || "User"}!
                    </h3>
                    <button
                      onClick={handleLogout}
                      className="group flex items-center justify-center space-x-3 sm:space-x-4 w-full bg-gradient-to-r from-[#61CE70] to-[#50B060] text-white font-medium py-2 sm:py-3 px-4 sm:px-6 rounded-xl transition-all duration-300 hover:shadow-lg hover:shadow-[#61CE70]/30 hover:scale-105"
                    >
                      <LogIn className="w-4 sm:w-5 h-4 sm:h-5 text-white group-hover:scale-110 transition-transform duration-300" />
                      <span>Log Out</span>
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center space-y-4 sm:space-y-6">
                <h3 className="textLg sm:text-xl font-bold text-white">
                  Sign In to Continue
                </h3>
                <p className="text-gray-400 text-sm sm:text-base">
                  Sign in with email or Google to get started.
                </p>
                <button
                  onClick={() =>
                    login({
                      loginMethods: ["google", "email", "sms", "passkey"],
                    })
                  }
                  className="group flex items-center justify-center space-x-3 sm:space-x-4 w-full bg-gradient-to-r from-[#61CE70] to-[#50B060] text-white font-medium py-2 sm:py-3 px-4 sm:px-6 rounded-xl transition-all duration-300 hover:shadow-lg hover:shadow-[#61CE70]/30 hover:scale-105"
                >
                  <LogIn className="w-4 sm:w-5 h-4 sm:h-5 text-white group-hover:scale-110 transition-transform duration-300" />
                  <span className="text-base sm:text-lg">Sign In</span>
                </button>
              </div>
            )}

            <a
              href="https://taptapup.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center justify-center space-x-3 sm:space-x-4 w-full mt-4 bg-gray-800/50 text-gray-300 font-medium py-2 sm:py-3 px-4 sm:px-6 rounded-xl transition-all duration-300 hover:bg-gradient-to-r hover:from-[#61CE70]/10 hover:to-[#50B060]/10 hover:text-white hover:border-[#61CE70]/30 border border-transparent"
            >
              <ArrowLeftFromLine className="w-4 sm:w-5 h-4 sm:h-5 transition-transform duration-200 group-hover:scale-110 group-hover:text-[#61CE70]" />
              <span className="text-sm sm:text-base">Visit TapTapUp</span>
            </a>

            <ul className="mt-6 bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/30 rounded-2xl p-4 sm:p-6 space-y-3 text-xs sm:text-sm text-gray-300">
              <li className="flex items-center space-x-3 p-3 bg-gradient-to-r from-[#61CE70]/10 to-[#50B060]/10 border border-[#61CE70]/30 rounded-xl group hover:shadow-lg hover:shadow-[#61CE70]/20 transition-all duration-300">
                <Shield className="w-5 h-5 text-[#61CE70] group-hover:scale-110 transition-transform duration-300" />
                <div className="flex flex-col">
                  <span className="text-sm font-semibold text-white">
                    Secured by SSL
                  </span>
                  <span className="text-xs text-gray-400">
                    Your data is encrypted and secure
                  </span>
                </div>
              </li>
              <li>
                PayzTap is registered with <strong>FinCEN</strong> (USA) as a{" "}
                <strong>Licensed Money Service Business</strong>.
              </li>
              <li>
                D-U-N-S® Number: <strong>13-968-1318</strong>
              </li>
              <li>
                BOIR ID: <strong>50000016298627</strong>
              </li>
            </ul>
          </div>
        </div>
      </div>
      {/* Powered By Section */}
      <section
        className=" overflow-hidden"
        aria-label="Trusted payment partners"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-5 md:mb-12">
            <h3 className="inline-block text-4xl sm:text-5xl font-black tracking-tight bg-gradient-to-r from-blue-500 via-cyan-400 to-purple-600 bg-clip-text text-transparent bg-[length:200%_200%] animate-gradient-shift">
              Powered By
            </h3>
          </div>

          <div className="relative rounded-3xl bg-white/5 backdrop-blur-xl border border-white/10 shadow-2xl overflow-hidden">
            <div className="py-10">
              <div className="flex animate-marquee-infinite gap-2 md:gap-20">
                {[...paymentLogos, ...paymentLogos].map((logo, i) => (
                  <div key={i} className="flex-shrink-0 px-1 md:px-4">
                    <img
                      src={logo.src}
                      alt={logo.alt}
                      className={`h-3 md:h-12 ${logo.scale} object-contain transition-all duration-300 hover:scale-125 hover:drop-shadow-2xl cursor-pointer`}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="pointer-events-none absolute inset-y-0 left-0 w-32 bg-gradient-to-r from-[#0F1419] to-transparent" />
            <div className="pointer-events-none absolute inset-y-0 right-0 w-32 bg-gradient-to-l from-[#0F1419] to-transparent" />
          </div>
        </div>

        <style>{`
                @keyframes marquee-infinite {
                  0% { transform: translateX(0%); }
                  100% { transform: translateX(-50%); }
                }
                @keyframes gradient-shift {
                  0%, 100% { background-position: 0% 50%; }
                  50% { background-position: 100% 50%; }
                }
                .animate-marquee-infinite {
                  animation: marquee-infinite 22s linear infinite;
                }
                .animate-marquee-infinite:hover {
                  animation-play-state: paused;
                }
                .animate-gradient-shift {
                  background-size: 200% 200%;
                  animation: gradient-shift 6s ease infinite;
                }
              `}</style>
      </section>
    </div>
  );
};

export default Login;
